import { PetType } from 'constants/pet-type';
import { gtm, selectElement } from 'tracking';
import {
  Action,
  DraftAction,
  DraftState,
  OptionType,
  State,
} from './interface';

/**
 * Utility function to track selected option.
 */
export const trackSelectedOption = (
  key: OptionType,
  selectedOption: string,
  petType?: 'cat' | 'dog'
): void => {
  const element = petType
    ? ['Need Capture', petType === 'cat' ? 'Cat' : 'Dog', key, selectedOption]
    : ['Need Capture', key, selectedOption];

  gtm(
    selectElement({
      element,
    })
  );
};

/**
 * Generate initial state for a specific pet type
 */
export const getInitialState = (
  petType: (typeof PetType)[keyof typeof PetType]
): State => ({
  isAgeSelectorOpen: false,
  isBreedSelectorOpen: false,
  isNeedSelectorOpen: false,
  isFoodTypeAndTasteSelectorOpen: false,
  selectedAge: undefined,
  selectedBreed: undefined,
  selectedNeeds: [],
  selectedAllergies: [],
  selectedFoodTypes: [],
  selectedWetFoodTypes: [],
  selectedTastePreferences: [],
  lastTouched: undefined,
  petType,
});

/**
 * Reducer for the need capture state.
 */
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TOGGLE_AGE_SELECTOR':
      return { ...state, isAgeSelectorOpen: !state.isAgeSelectorOpen };
    case 'TOGGLE_BREED_SELECTOR':
      return { ...state, isBreedSelectorOpen: !state.isBreedSelectorOpen };
    case 'TOGGLE_NEED_SELECTOR':
      return { ...state, isNeedSelectorOpen: !state.isNeedSelectorOpen };
    case 'TOGGLE_FOOD_TYPE_AND_TASTE_SELECTOR':
      return {
        ...state,
        isFoodTypeAndTasteSelectorOpen: !state.isFoodTypeAndTasteSelectorOpen,
      };
    case 'RESET_STATE':
      return getInitialState(action.payload);
    case 'SET_AGE': {
      const payload =
        action.payload === state.selectedAge ? undefined : action.payload;
      if (payload) trackSelectedOption('Age', payload, state.petType);

      return {
        ...state,
        selectedAge: payload,
        lastTouched: action.payload,
      };
    }
    case 'SET_BREED': {
      if (state.petType !== 'dog') return state;
      const payload =
        action.payload === state.selectedBreed ? undefined : action.payload;
      if (payload) trackSelectedOption('Breed', action.payload);
      return {
        ...state,
        selectedBreed: payload,
        lastTouched: action.payload,
      };
    }
    case 'SET_NEEDS':
      if (action.payload.length > 0) {
        trackSelectedOption('Need', action.payload.sort().join(','));
      }
      return {
        ...state,
        selectedNeeds: action.payload,
        lastTouched:
          action.payload[action.payload.length - 1] || state.lastTouched,
      };
    case 'SET_ALLERGIES':
      if (action.payload.length > 0) {
        trackSelectedOption('Allergy', action.payload.sort().join(','));
      }
      return { ...state, selectedAllergies: action.payload };
    case 'SET_FOOD_TYPES':
      if (action.payload.length > 0) {
        trackSelectedOption(
          'Food type',
          action.payload.sort().join(','),
          state.petType
        );
      }
      return { ...state, selectedFoodTypes: action.payload };
    case 'SET_WET_FOOD_TYPES':
      if (state.petType === 'cat' && action.payload.length > 0) {
        trackSelectedOption(
          'Consistency',
          action.payload.sort().join(','),
          state.petType
        );
      }
      return state.petType === 'cat'
        ? { ...state, selectedWetFoodTypes: action.payload }
        : state;
    case 'SET_TASTE_PREFERENCE_PREFERENCES':
      return state.petType === 'cat'
        ? { ...state, selectedTastePreferences: action.payload }
        : state;
    case 'SET_MULTIPLE': {
      const baseState = {
        ...state,
        ...action.payload,
      };

      return baseState.petType === 'dog'
        ? ({
            ...baseState,
            selectedWetFoodTypes: [],
            selectedTastePreferences: [],
          } as State)
        : ({ ...baseState, selectedBreed: undefined } as State);
    }
    default:
      throw new Error();
  }
};

/**
 * Reducer for the draft state of needs and allergies.
 */
export const draftStateReducer = (
  state: DraftState,
  action: DraftAction
): DraftState => {
  switch (action.type) {
    case 'SET_DRAFT_NEEDS':
      return {
        ...state,
        draftNeeds: action.payload,
        lastTouched:
          action.payload[action.payload.length - 1] || state.lastTouched,
      };
    case 'SET_DRAFT_ALLERGIES':
      return {
        ...state,
        draftAllergies: action.payload,
      };
    case 'SET_DRAFT_FOOD_TYPES':
      return {
        ...state,
        draftFoodTypes: action.payload,
      };
    case 'SET_DRAFT_WET_FOOD_TYPES':
      return {
        ...state,
        draftWetFoodTypes: action.payload,
      };
    case 'SET_DRAFT_TASTE_PREFERENCES':
      return {
        ...state,
        draftTastePreferences: action.payload,
      };
    case 'SET_MULTIPLE':
      return {
        ...state,
        ...action.payload,
      };
    default:
      throw new Error();
  }
};
